export type LoggedInAccount = {
  /**
   * 用于唯一标识账号的 key（如：username 或 mobile）
   * 注意：这里不依赖后端返回的用户 id，避免后端未在登录接口透出时无法存储。
   */
  id: string;
  /** 用于展示的账号信息（如：admin / 138xxxxxx） */
  label: string;
  /** 该账号对应的登录 token */
  token: string;
  /** 最近登录时间（毫秒） */
  lastLoginAt: number;
};

const LOGGED_IN_ACCOUNTS_KEY = 'qn_logged_in_accounts';
const ACTIVE_ACCOUNT_ID_KEY = 'qn_active_logged_in_account_id';

function safeJsonParse<T>(raw: string | null): T | undefined {
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
}

export function getLoggedInAccounts(): LoggedInAccount[] {
  if (typeof window === 'undefined') return [];
  try {
    const list = safeJsonParse<LoggedInAccount[]>(window.localStorage.getItem(LOGGED_IN_ACCOUNTS_KEY));
    if (!Array.isArray(list)) return [];
    return list
      .filter((it) => it && typeof it.id === 'string' && typeof it.token === 'string')
      .sort((a, b) => (b.lastLoginAt ?? 0) - (a.lastLoginAt ?? 0));
  } catch {
    return [];
  }
}

export function getActiveAccountId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const id = window.localStorage.getItem(ACTIVE_ACCOUNT_ID_KEY);
    return id && id.trim() ? id : null;
  } catch {
    return null;
  }
}

export function setActiveAccountId(id: string) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(ACTIVE_ACCOUNT_ID_KEY, id);
  } catch {
    // ignore
  }
}

export function upsertLoggedInAccount(input: Omit<LoggedInAccount, 'lastLoginAt'>) {
  if (typeof window === 'undefined') return;
  try {
    const list = getLoggedInAccounts();
    const now = Date.now();
    const idx = list.findIndex((a) => a.id === input.id);
    const next: LoggedInAccount = { ...input, lastLoginAt: now };
    if (idx >= 0) {
      list[idx] = { ...list[idx], ...next };
    } else {
      list.unshift(next);
    }
    window.localStorage.setItem(LOGGED_IN_ACCOUNTS_KEY, JSON.stringify(list));
  } catch {
    // ignore
  }
}

export function removeLoggedInAccount(id: string) {
  if (typeof window === 'undefined') return;
  try {
    const list = getLoggedInAccounts().filter((a) => a.id !== id);
    window.localStorage.setItem(LOGGED_IN_ACCOUNTS_KEY, JSON.stringify(list));
    const activeId = getActiveAccountId();
    if (activeId === id) {
      window.localStorage.removeItem(ACTIVE_ACCOUNT_ID_KEY);
    }
  } catch {
    // ignore
  }
}

